import React, { useState, useEffect } from 'react';
import {
	Container,
	Card,
	Row,
	Col,
	Form,
	Button,
	Alert,
	Badge,
	Table,
	ProgressBar,
} from 'react-bootstrap';
import { useAuth } from '../../contexts/AuthContext/AuthContext';
import { db } from '../../firebase';
import {
	collection,
	addDoc,
	query,
	where,
	getDocs,
	orderBy,
	deleteDoc,
	doc,
	Timestamp,
} from 'firebase/firestore';

const PracticeLog = () => {
	const { currentUser } = useAuth();
	const [practiceEntries, setPracticeEntries] = useState([]);
	const [loading, setLoading] = useState(true);
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState('');
	const [success, setSuccess] = useState('');

	// Form state
	const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
	const [minutes, setMinutes] = useState('');
	const [notes, setNotes] = useState('');

	useEffect(() => {
		fetchPracticeEntries();
	}, [currentUser]);

	const fetchPracticeEntries = async () => {
		if (!currentUser) return;

		try {
			const practiceQuery = query(
				collection(db, 'practiceLog'),
				where('userId', '==', currentUser.uid),
				orderBy('date', 'desc')
			);
			const querySnapshot = await getDocs(practiceQuery);
			const entries = querySnapshot.docs.map((doc) => ({
				id: doc.id,
				...doc.data(),
				date: doc.data().date.toDate(),
			}));
			setPracticeEntries(entries);
		} catch (error) {
			console.error('Error fetching practice entries:', error);
			setError('Failed to load practice log');
		} finally {
			setLoading(false);
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setError('');
		setSuccess('');
		setSubmitting(true);

		if (!minutes || parseInt(minutes) <= 0) {
			setError('Please enter a valid number of minutes');
			setSubmitting(false);
			return;
		}

		try {
			await addDoc(collection(db, 'practiceLog'), {
				userId: currentUser.uid,
				date: Timestamp.fromDate(new Date(date)),
				minutes: parseInt(minutes),
				notes: notes.trim(),
				createdAt: Timestamp.now(),
			});

			setSuccess('Practice session logged successfully!');
			setMinutes('');
			setNotes('');
			setDate(new Date().toISOString().split('T')[0]);

			// Refresh entries
			await fetchPracticeEntries();

			// Clear success message after 3 seconds
			setTimeout(() => setSuccess(''), 3000);
		} catch (error) {
			console.error('Error logging practice:', error);
			setError('Failed to log practice session');
		} finally {
			setSubmitting(false);
		}
	};

	const handleDelete = async (entryId) => {
		if (!window.confirm('Are you sure you want to delete this entry?')) return;

		try {
			await deleteDoc(doc(db, 'practiceLog', entryId));
			setSuccess('Entry deleted successfully');
			await fetchPracticeEntries();
			setTimeout(() => setSuccess(''), 3000);
		} catch (error) {
			console.error('Error deleting entry:', error);
			setError('Failed to delete entry');
		}
	};

	// Calculate weekly stats
	const getWeeklyStats = () => {
		const oneWeekAgo = new Date();
		oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

		const weeklyEntries = practiceEntries.filter(
			(entry) => entry.date >= oneWeekAgo
		);
		const totalMinutes = weeklyEntries.reduce(
			(sum, entry) => sum + entry.minutes,
			0
		);
		const daysCount = weeklyEntries.length;

		return { totalMinutes, daysCount };
	};

	const { totalMinutes, daysCount } = getWeeklyStats();

	// Get recommended practice based on typical age ranges
	const getRecommendation = () => {
		return {
			younger: { minutes: 30, days: 4, label: 'Ages 8-12' },
			older: { minutes: 60, days: 5, label: 'Ages 13+' },
		};
	};

	const recommendations = getRecommendation();

	if (loading) {
		return (
			<Container className='py-5'>
				<div className='text-center'>Loading...</div>
			</Container>
		);
	}

	return (
		<Container className='py-5'>
			<h1 className='mb-4' style={{ color: '#384971' }}>
				<i className='fa-sharp fa-solid fa-music me-2'></i>
				Practice Log
			</h1>

			<Alert variant='info' className='mb-4'>
				<Alert.Heading>Practice Guidelines</Alert.Heading>
				<p className='mb-2'>
					Like training for a sport, consistency is key to musical growth:
				</p>
				<ul className='mb-0'>
					<li>
						<strong>{recommendations.younger.label}:</strong>{' '}
						{recommendations.younger.minutes}-60 minutes total,{' '}
						{recommendations.younger.days}-5 days/week
					</li>
					<li>
						<strong>{recommendations.older.label}:</strong>{' '}
						{recommendations.older.minutes}+ minutes total,{' '}
						{recommendations.older.days}-6 days/week
					</li>
				</ul>
			</Alert>

			{/* Weekly Stats */}
			<Row className='g-4 mb-4'>
				<Col md={6}>
					<Card className='h-100 shadow-sm'>
						<Card.Body>
							<Card.Title>This Week</Card.Title>
							<div className='d-flex align-items-center mb-3'>
								<div className='me-4'>
									<h3 className='mb-0' style={{ color: '#384971' }}>
										{Math.floor(totalMinutes / 60)}h {totalMinutes % 60}m
									</h3>
									<small className='text-muted'>Total Time</small>
								</div>
								<div>
									<h3 className='mb-0' style={{ color: '#384971' }}>
										{daysCount}
									</h3>
									<small className='text-muted'>Days</small>
								</div>
							</div>
							<div className='mb-2'>
								<small className='text-muted'>
									Progress toward {recommendations.younger.days} days/week
								</small>
								<ProgressBar
									now={(daysCount / recommendations.younger.days) * 100}
									variant={
										daysCount >= recommendations.younger.days
											? 'success'
											: 'primary'
									}
								/>
							</div>
						</Card.Body>
					</Card>
				</Col>

				<Col md={6}>
					<Card className='h-100 shadow-sm bg-light'>
						<Card.Body>
							<Card.Title>Quick Tips</Card.Title>
							<ul className='mb-0' style={{ fontSize: '0.9rem' }}>
								<li>Take breaks during longer practice sessions</li>
								<li>Stop if you experience discomfort</li>
								<li>Practice at the same time each day to build routine</li>
								<li>Focus on quality over quantity</li>
							</ul>
						</Card.Body>
					</Card>
				</Col>
			</Row>

			{/* Log New Practice Session */}
			<Card className='shadow-sm mb-4'>
				<Card.Body>
					<Card.Title>
						<i className='fa-sharp fa-solid fa-plus-circle me-2'></i>
						Log Practice Session
					</Card.Title>

					{error && <Alert variant='danger'>{error}</Alert>}
					{success && <Alert variant='success'>{success}</Alert>}

					<Form onSubmit={handleSubmit}>
						<Row>
							<Col md={4}>
								<Form.Group className='mb-3'>
									<Form.Label>Date</Form.Label>
									<Form.Control
										type='date'
										value={date}
										onChange={(e) => setDate(e.target.value)}
										max={new Date().toISOString().split('T')[0]}
										required
									/>
								</Form.Group>
							</Col>
							<Col md={4}>
								<Form.Group className='mb-3'>
									<Form.Label>Minutes Practiced</Form.Label>
									<Form.Control
										type='number'
										placeholder='30'
										value={minutes}
										onChange={(e) => setMinutes(e.target.value)}
										min='1'
										max='300'
										required
									/>
								</Form.Group>
							</Col>
							<Col md={4}>
								<Form.Group className='mb-3'>
									<Form.Label>&nbsp;</Form.Label>
									<Button
										type='submit'
										variant='primary'
										className='w-100'
										disabled={submitting}
									>
										{submitting ? 'Logging...' : 'Log Practice'}
									</Button>
								</Form.Group>
							</Col>
						</Row>
						<Form.Group className='mb-0'>
							<Form.Label>Notes (optional)</Form.Label>
							<Form.Control
								as='textarea'
								rows={2}
								placeholder='What did you work on today?'
								value={notes}
								onChange={(e) => setNotes(e.target.value)}
								maxLength={500}
							/>
						</Form.Group>
					</Form>
				</Card.Body>
			</Card>

			{/* Practice History */}
			<Card className='shadow-sm'>
				<Card.Body>
					<Card.Title>Practice History</Card.Title>

					{practiceEntries.length === 0 ? (
						<Alert variant='secondary'>
							No practice sessions logged yet. Start logging your practice
							to track your progress!
						</Alert>
					) : (
						<Table responsive hover>
							<thead>
								<tr>
									<th>Date</th>
									<th>Duration</th>
									<th>Notes</th>
									<th></th>
								</tr>
							</thead>
							<tbody>
								{practiceEntries.map((entry) => (
									<tr key={entry.id}>
										<td>
											{entry.date.toLocaleDateString('en-US', {
												weekday: 'short',
												month: 'short',
												day: 'numeric',
											})}
										</td>
										<td>
											<Badge bg='primary'>
												{entry.minutes} min
											</Badge>
										</td>
										<td>
											<small className='text-muted'>
												{entry.notes || '—'}
											</small>
										</td>
										<td className='text-end'>
											<Button
												variant='outline-danger'
												size='sm'
												onClick={() => handleDelete(entry.id)}
											>
												<i className='fa-sharp fa-solid fa-trash'></i>
											</Button>
										</td>
									</tr>
								))}
							</tbody>
						</Table>
					)}
				</Card.Body>
			</Card>
		</Container>
	);
};

export default PracticeLog;
